import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Checkbox, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface FacilityOption {
  id: string;
  name: string;
  slug: string;
  facilityType: string;
  parentTenantId: string | null;
  childCount: number;
}

interface GroupResponse {
  rootId: string;
  rootName: string;
  isRoot: boolean;
  members: { id: string; name: string; slug: string }[];
}

/**
 * The chain a facility belongs to (LIMS parity).
 *
 * One level only: a flagship and its sister sites. The list ticked here is the
 * whole membership — anything left unticked is detached — which is why the
 * dialog shows what is currently in the group before anything is changed.
 */
export function FacilityGroupDialog({
  tenant,
  open,
  onClose,
}: {
  tenant: { id: string; name: string; facilityType: string; parentTenantId: string | null };
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<string[]>([]);
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const group = useQuery({
    queryKey: ['tenant-group', tenant.id],
    queryFn: () => get<GroupResponse>(`/platform/tenants/${tenant.id}/group`),
    enabled: open,
  });

  const facilities = useQuery({
    queryKey: ['tenants', 'group-picker', tenant.facilityType],
    queryFn: () =>
      get<{ data: FacilityOption[] }>(`/platform/tenants?facilityType=${tenant.facilityType}&limit=100`),
    enabled: open,
  });

  useEffect(() => {
    if (group.data) setChosen(group.data.members.map((m) => m.id));
  }, [group.data]);

  // Only facilities of the same kind, and never one that already heads a group
  // of its own or belongs to someone else's — the server refuses those, and
  // offering them would only produce an error the operator cannot act on.
  const candidates = (facilities.data?.data ?? []).filter(
    (f) =>
      f.id !== tenant.id &&
      f.childCount === 0 &&
      (f.parentTenantId === null || f.parentTenantId === tenant.id),
  );

  const save = useMutation({
    mutationFn: () =>
      put<GroupResponse>(`/platform/tenants/${tenant.id}/group`, { totpCode, childTenantIds: chosen }),
    onSuccess: (data) => {
      toast.success(
        data.members.length === 0
          ? `${tenant.name} is no longer a group`
          : `${data.members.length} site${data.members.length === 1 ? '' : 's'} in ${tenant.name}'s group`,
      );
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      void queryClient.invalidateQueries({ queryKey: ['tenant-group'] });
      close();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const close = () => {
    setTotpCode('');
    setError(null);
    onClose();
  };

  // A sister site cannot head a group; the flagship is where membership is set.
  if (group.data && !group.data.isRoot) {
    return (
      <Dialog
        open={open}
        onClose={close}
        title="Part of a group"
        description={`${tenant.name} is a site of ${group.data.rootName}`}
        footer={<Button onClick={close}>Close</Button>}
      >
        <p className="text-sm text-muted-foreground">
          Membership is set from the group&apos;s flagship. Open <strong>{group.data.rootName}</strong> and change
          it there — a group is one level deep, so a site never heads a group of its own.
        </p>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title={`${tenant.name}'s group`}
      description="A chain's flagship and its sister sites — one level, all the same kind of facility"
      footer={
        <>
          {totpCode.trim().length < 6 && (
            <span className="me-auto self-center text-xs text-muted-foreground">
              Still needed: your authenticator code
            </span>
          )}
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            loading={save.isPending}
            disabled={totpCode.trim().length < 6}
          >
            Save group
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No other free facility of this kind. A site already in another group, or one that heads a group of
            its own, cannot join this one.
          </p>
        ) : (
          <div>
            <p className="mb-2 text-sm font-medium">
              Sites in this group
              <span className="ms-2 font-normal text-muted-foreground">
                {chosen.length} of {candidates.length} ticked
              </span>
            </p>
            <div className="grid gap-1.5">
              {candidates.map((facility) => (
                <label
                  key={facility.id}
                  className={`flex cursor-pointer items-center gap-2 rounded-md border p-2 text-sm ${
                    chosen.includes(facility.id) ? 'border-primary/40 bg-primary/5' : ''
                  }`}
                >
                  <Checkbox
                    checked={chosen.includes(facility.id)}
                    onCheckedChange={(checked) =>
                      setChosen((prev) =>
                        checked ? [...prev, facility.id] : prev.filter((id) => id !== facility.id),
                      )
                    }
                  />
                  <span className="me-auto">{facility.name}</span>
                  <Badge variant="muted">{facility.slug}</Badge>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              What is ticked here is the whole membership — anything left unticked is detached from the group.
            </p>
          </div>
        )}

        <Field label="Authenticator code" required>
          <Input
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            className="w-32 tabular"
          />
        </Field>

        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
